import { useEffect, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";
import { LogOut, Users, DollarSign, Crown, BarChart3, Compass, ChevronRight, Check, Clock, MessageCircle, TrendingUp, Megaphone, Target, Radio, Heart, ToggleLeft, ToggleRight, Shield } from "lucide-react";

export default function AdmiralDashboard() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [audience, setAudience] = useState([]);
  const [theaters, setTheaters] = useState([]);
  const [payments, setPayments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [leads, setLeads] = useState([]);
  const [signals, setSignals] = useState([]);
  const [heir, setHeir] = useState(null);
  const [togglingHeir, setTogglingHeir] = useState(false);
  const [paymentMode, setPaymentMode] = useState(null);

  const loadAll = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch((e) => console.error(e));
    api.get("/admin/applications").then((r) => setApps(r.data)).catch((e) => console.error(e));
    api.get("/admin/audience").then((r) => setAudience(r.data)).catch((e) => console.error(e));
    api.get("/admin/future-theaters").then((r) => setTheaters(r.data)).catch((e) => console.error(e));
    api.get("/admin/payments").then((r) => setPayments(r.data)).catch((e) => console.error(e));
    api.get("/admin/marketing/campaigns").then((r) => setCampaigns(r.data)).catch((e) => console.error(e));
    api.get("/admin/marketing/leads").then((r) => setLeads(r.data)).catch((e) => console.error(e));
    api.get("/admin/marketing/signals").then((r) => setSignals(r.data)).catch((e) => console.error(e));
    api.get("/admin/heir/status").then((r) => setHeir(r.data)).catch((e) => console.error(e));
    api.get("/payments/mode").then((r) => setPaymentMode(r.data)).catch((e) => console.error(e));
  }, []);

  useEffect(() => {
    if (user && user.role === "admin") loadAll();
  }, [user, loadAll]);

  if (loading) return <Layout><div className="px-6 py-32 text-white/40">Authorising…</div></Layout>;
  if (!user || user.role !== "admin") return <Navigate to="/admiral/login" replace />;

  const fullUrl = (path) => (path?.startsWith("http") ? path : `${API_BASE.replace(/\/api$/, "")}${path}`);

  const toggleHeir = async () => {
    if (!heir) return;
    setTogglingHeir(true);
    try {
      const { data } = await api.post("/admin/heir/toggle", { active: !heir.active });
      setHeir(data);
      // Refresh stats to reflect new vault balance computation
      api.get("/admin/stats").then((r) => setStats(r.data)).catch((e) => console.error(e));
      toast.success(`Mother's Designation ${data.active ? "ACTIVATED" : "PAUSED"} · Ashairah Heir Vault ${data.active ? "now receiving 60%" : "not receiving share"}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Toggle failed");
    } finally {
      setTogglingHeir(false);
    }
  };

  return (
    <Layout>
      <section className="relative py-12 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-2">
            <div>
              <span className="label-gold">Admiral Dashboard · {user.name}</span>
              <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tighter text-white mt-2">
                Command <span className="gold-shimmer">Bridge</span>.
              </h1>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#C9A227] mt-2">
                (Oversight by Akanksha and Director K)
              </p>
              {paymentMode?.mode && (
                <div className="flex items-center gap-2 mt-3">
                  <span
                    data-testid="admin-payment-mode-badge"
                    className={`text-[10px] tracking-[0.3em] uppercase font-bold px-3 py-1.5 ${
                      paymentMode.mode === "live"
                        ? "text-[#050505] bg-[#C9A227] border border-[#C9A227]"
                        : "text-white/60 border border-white/20"
                    }`}
                  >
                    {paymentMode.mode === "live"
                      ? "⚡ Stripe · LIVE Mode"
                      : paymentMode.mode === "test"
                      ? "Stripe · Test Mode"
                      : "Stripe · Mode Unknown"}
                  </span>
                  <span className="text-white/30 text-[10px] uppercase tracking-widest">
                    ${paymentMode.amount} {(paymentMode.currency || "").toUpperCase()}
                  </span>
                </div>
              )}
            </div>
            <button onClick={logout} className="btn-outline-gold text-[10px]" data-testid={ADMIN.logoutBtn}>
              <LogOut size={12} className="inline -mt-0.5 mr-1"/> Logout
            </button>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-1 my-8 border-b border-[#C9A227]/15">
            {[
              { k: "overview", l: "Overview" },
              { k: "roster", l: `Command Roster (${apps.length})` },
              { k: "marketing", l: `Marketing Hub (${campaigns.length + leads.length + signals.length})` },
              { k: "heir", l: `Parental Bridge` },
              { k: "audience", l: `Audience (${audience.length})` },
              { k: "theaters", l: `Future Theaters (${theaters.length})` },
              { k: "payments", l: `Payments (${payments.length})` },
            ].map((t) => (
              <button key={t.k} onClick={() => setTab(t.k)}
                data-testid={`admin-tab-${t.k}`}
                className={`px-5 py-3 text-[10px] tracking-[0.25em] uppercase font-bold transition-colors ${
                  tab === t.k ? "text-[#C9A227] border-b-2 border-[#C9A227]" : "text-white/40 hover:text-white"
                }`}>
                {t.l}
              </button>
            ))}
          </div>

          {tab === "overview" && stats && (
            <div className="space-y-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard icon={<Users/>} label="Applications" value={stats.total_applications} sub={`${stats.submitted_applications} submitted`}/>
                <StatCard icon={<DollarSign/>} label="Gross Revenue" value={`$${stats.gross_revenue.toFixed(0)}`} sub="Boarding deposits"/>
                <StatCard icon={<TrendingUp/>} label="Total Votes" value={stats.total_votes} sub="Email-verified"/>
                <StatCard icon={<MessageCircle/>} label="Audience" value={stats.audience_count} sub="Sovereign list"/>
              </div>

              {/* 60/30/10 Lineage Law */}
              <div className="glass border border-[#C9A227]/30 p-7" style={{borderRadius:0}}>
                <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                  <div className="flex items-center gap-3">
                    <Crown className="text-[#C9A227]"/>
                    <h3 className="text-white text-xl font-black uppercase tracking-tight">60 / 30 / 10 Lineage Law</h3>
                  </div>
                  {stats.heir_designation_active && (
                    <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#C9A227] border border-[#C9A227] px-3 py-1.5">
                      ✦ Mother's Designation Active — Admiral 60% → Heir Vault
                    </span>
                  )}
                </div>
                <div className="grid md:grid-cols-4 gap-5">
                  <SplitCard label={stats.heir_designation_active ? "Admiral · 60% → HEIR" : "Admiral · 60%"} value={stats.lineage_law.admiral_60} accent/>
                  <SplitCard label="Lineage · 30%" value={stats.lineage_law.lineage_30}/>
                  <SplitCard label="EIGOO · 10%" value={stats.lineage_law.eigoo_10}/>
                  <SplitCard label={`Ashairah Heir Vault`} value={stats.heir_vault_balance} accent={stats.heir_designation_active}/>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div className="glass border border-[#C9A227]/20 p-7">
                  <BarChart3 className="text-[#C9A227]"/>
                  <h4 className="text-white font-black uppercase tracking-tight mt-3">Pipeline</h4>
                  <div className="space-y-2 mt-4 text-sm">
                    <PipelineRow label="Pending Payment" value={stats.pending_applications}/>
                    <PipelineRow label="Submitted" value={stats.submitted_applications}/>
                    <PipelineRow label="Active Students" value={stats.active_students}/>
                    <PipelineRow label="Active Courses" value={stats.active_courses}/>
                  </div>
                </div>
                <div className="glass border border-[#C9A227]/20 p-7">
                  <Compass className="text-[#C9A227]"/>
                  <h4 className="text-white font-black uppercase tracking-tight mt-3">Future Theaters</h4>
                  <p className="text-white/55 text-sm mt-2">
                    {stats.future_theaters_count} territory requests captured from non-beauty operators.
                  </p>
                  <button onClick={() => setTab("theaters")} className="btn-outline-gold text-[10px] mt-4">
                    Review Theaters <ChevronRight size={12} className="inline -mt-0.5"/>
                  </button>
                </div>
              </div>
            </div>
          )}

          {tab === "marketing" && (
            <div className="space-y-8">
              {/* Campaign Dashboard */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Megaphone className="text-[#C9A227]"/>
                  <h3 className="text-white text-lg font-black uppercase tracking-tight">Campaign Dashboard</h3>
                  <span className="text-[10px] tracking-widest uppercase text-white/40">{campaigns.length} active</span>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {campaigns.map((c) => (
                    <div key={c.id} className="glass border border-[#C9A227]/15 p-5">
                      <div className="flex items-baseline justify-between gap-3 mb-2">
                        <div className="text-white font-bold uppercase tracking-tight truncate">{c.name}</div>
                        <span className={`text-[9px] tracking-[0.25em] uppercase font-bold ${c.status === "live" ? "text-green-400" : c.status === "draft" ? "text-yellow-400" : "text-white/40"}`}>{c.status}</span>
                      </div>
                      <div className="text-[#C9A227] text-[10px] tracking-widest uppercase mb-3">{c.channel}</div>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <Stat label="Spend / Budget" v={`$${c.spend_usd}/${c.budget_usd}`}/>
                        <Stat label="Impressions" v={c.impressions.toLocaleString()}/>
                        <Stat label="Clicks" v={c.clicks.toLocaleString()}/>
                        <Stat label="Conversions" v={c.conversions}/>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Lead Tracker */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Target className="text-[#C9A227]"/>
                  <h3 className="text-white text-lg font-black uppercase tracking-tight">Lead Tracker</h3>
                  <span className="text-[10px] tracking-widest uppercase text-white/40">{leads.length} leads</span>
                </div>
                <div className="space-y-2">
                  {leads.map((l) => (
                    <div key={l.id} className="glass border border-[#C9A227]/15 p-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-center text-sm">
                      <div className="text-white font-bold truncate">{l.name}</div>
                      <div className="text-white/60 truncate">{l.email}</div>
                      <div className="text-[#C9A227] text-[10px] tracking-widest uppercase">{l.source}</div>
                      <div className={`text-[10px] tracking-widest uppercase font-bold ${l.stage === "qualified" ? "text-green-400" : l.stage === "contacted" ? "text-yellow-400" : "text-white/40"}`}>{l.stage}</div>
                      <div className="text-white tabular-nums">${l.value_usd}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Social Signal Monitor */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Radio className="text-[#C9A227]"/>
                  <h3 className="text-white text-lg font-black uppercase tracking-tight">Social Signal Monitor</h3>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {signals.map((s) => (
                    <div key={s.id} className="glass border border-[#C9A227]/15 p-5">
                      <div className="flex items-baseline justify-between mb-2">
                        <span className="text-[10px] tracking-[0.25em] uppercase text-[#C9A227] font-bold">{s.platform}</span>
                        <span className={`text-[10px] tracking-widest uppercase font-bold ${s.trend === "up" ? "text-green-400" : s.trend === "down" ? "text-red-400" : "text-white/40"}`}>▲ {s.trend}</span>
                      </div>
                      <div className="text-white text-sm font-bold">{s.handle}</div>
                      <div className="text-3xl font-black gold-shimmer tabular-nums mt-2">{s.value.toLocaleString()}</div>
                      <div className="text-[10px] tracking-widest uppercase text-white/40 mt-1">{s.metric}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "heir" && heir && (
            <div className="space-y-6">
              <div className="glass border border-[#C9A227]/40 p-7" style={{borderRadius:0}}>
                <div className="flex items-start justify-between flex-wrap gap-5">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <Shield className="text-[#C9A227]"/>
                      <h3 className="text-white text-xl font-black uppercase tracking-tight">Parental Bridge · Ashairah Flagship</h3>
                    </div>
                    <p className="text-white/60 text-sm max-w-xl">
                      Hard-wired link between <span className="text-[#C9A227] font-bold">{heir.guardian.name}</span> (Guardian) and <span className="text-[#C9A227] font-bold">{heir.heir.name}</span> (Heir).
                      Mother's Designation toggle redirects the Admiral's 60% share into Ashairah's Heir Vault.
                    </p>
                  </div>
                  <button onClick={toggleHeir} disabled={togglingHeir} data-testid="heir-toggle-btn"
                    className={`flex items-center gap-3 px-5 py-3 border-2 transition-all ${heir.active ? "border-[#C9A227] bg-[#C9A227]/10" : "border-white/20"}`}>
                    {heir.active ? <ToggleRight className="text-[#C9A227]" size={28}/> : <ToggleLeft className="text-white/50" size={28}/>}
                    <span className={`text-[11px] tracking-[0.3em] uppercase font-black ${heir.active ? "text-[#C9A227]" : "text-white/60"}`}>
                      {togglingHeir ? "…" : heir.active ? "Designation Active" : "Designation Paused"}
                    </span>
                  </button>
                </div>

                <div className="grid md:grid-cols-3 gap-5 mt-7">
                  <div className="border border-[#C9A227]/30 p-5">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A227] font-bold">Heir Vault Balance</div>
                    <div className="text-4xl font-black gold-shimmer tabular-nums mt-2">${heir.balance.toFixed(2)}</div>
                    <div className="text-[10px] tracking-widest uppercase text-white/40 mt-1">{heir.ledger.length} ledger entries</div>
                  </div>
                  <div className="border border-[#C9A227]/20 p-5">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A227] font-bold">Heir</div>
                    <div className="text-2xl font-black text-white mt-2">{heir.heir.name}</div>
                    <div className="text-[10px] tracking-widest uppercase text-white/40 mt-1">Parent → {heir.heir.parent_id}</div>
                  </div>
                  <div className="border border-[#C9A227]/20 p-5">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A227] font-bold">Guardian Access</div>
                    <div className="text-sm text-white mt-2">{heir.guardian.email}</div>
                    <Link to="/guardian" className="text-[#C9A227] text-[10px] tracking-widest uppercase font-bold mt-2 inline-block hover:underline">
                      Open Guardian Dashboard <ChevronRight size={12} className="inline -mt-0.5"/>
                    </Link>
                  </div>
                </div>
              </div>

              {/* Ledger */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Heart className="text-[#C9A227]"/>
                  <h3 className="text-white text-lg font-black uppercase tracking-tight">Heir Vault Ledger</h3>
                </div>
                {heir.ledger.length === 0 ? (
                  <p className="text-white/40 py-6 text-center text-sm border border-[#C9A227]/10">No transactions yet. Activate the toggle and complete a $99 boarding to seed the vault.</p>
                ) : (
                  <div className="space-y-2">
                    {heir.ledger.map((entry) => (
                      <div key={entry.id} className="glass border border-[#C9A227]/15 p-4 grid grid-cols-3 gap-3 text-sm items-center">
                        <div className="text-[#C9A227] text-[10px] tracking-widest uppercase font-bold">{entry.source}</div>
                        <div className="text-white tabular-nums font-bold">+${entry.amount.toFixed(2)}</div>
                        <div className="text-white/40 text-xs text-right">{entry.created_at?.slice(0, 19).replace("T", " ")}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Audit history */}
              {heir.history?.length > 0 && (
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-white/40 mb-2">Designation Audit Trail</div>
                  <div className="space-y-1.5 text-xs">
                    {heir.history.slice(-10).reverse().map((h, i) => (
                      <div key={`${h.at}-${i}`} className="text-white/55">
                        <span className={h.active ? "text-[#C9A227]" : "text-white/50"}>{h.active ? "ACTIVATED" : "PAUSED"}</span>
                        {" "}· {h.at?.slice(0, 19).replace("T", " ")} · by {h.by}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "roster" && (
            <div className="space-y-3">
              {apps.length === 0 ? (
                <p className="text-white/40 py-10 text-center text-sm">Command Roster is empty.</p>
              ) : apps.map((a) => (
                <div key={a.id} className="glass border border-[#C9A227]/15 p-5 grid md:grid-cols-[1fr_2fr_1fr_auto] gap-4 items-center" data-testid={ADMIN.rosterRow}>
                  <div>
                    <div className="text-white font-bold uppercase tracking-tight">{a.full_name}</div>
                    <div className="text-white/40 text-xs">{a.email}</div>
                    {a.raffle_ticket && <div className="text-[#C9A227] text-[10px] tracking-widest mt-1">{a.raffle_ticket}</div>}
                  </div>
                  <div className="text-white/70 text-sm truncate">{a.niche}</div>
                  <div>
                    {a.status === "submitted" ? (
                      <span className="text-[10px] tracking-[0.25em] uppercase text-green-400 flex items-center gap-1"><Check size={12}/> Paid</span>
                    ) : (
                      <span className="text-[10px] tracking-[0.25em] uppercase text-yellow-400 flex items-center gap-1"><Clock size={12}/> Pending</span>
                    )}
                    <div className="text-white/40 text-[10px] tracking-widest uppercase mt-1">▲ {a.upvotes || 0}</div>
                  </div>
                  {a.status === "submitted" && (
                    <Link to={`/candidates/${a.id}`} className="text-[#C9A227] text-[10px] tracking-[0.25em] uppercase font-bold hover:underline">
                      View Profile
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === "audience" && (
            <div className="space-y-2">
              {audience.length === 0 ? (
                <p className="text-white/40 py-10 text-center text-sm">No voters captured yet.</p>
              ) : audience.map((a) => (
                <div key={a.id} className="glass border border-[#C9A227]/15 p-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="text-white font-bold truncate">{a.name}</div>
                  <div className="text-white/60 truncate">{a.email}</div>
                  <div className="text-[#C9A227] text-[10px] tracking-widest uppercase">{a.source}</div>
                  <div className="text-white/40 text-[10px] tracking-widest uppercase">{a.interactions || 0} interactions</div>
                </div>
              ))}
            </div>
          )}

          {tab === "theaters" && (
            <div className="space-y-3">
              {theaters.length === 0 ? (
                <p className="text-white/40 py-10 text-center text-sm">No future theater requests yet.</p>
              ) : theaters.map((t) => (
                <div key={t.id} className="glass border border-[#C9A227]/15 p-5">
                  <div className="flex items-baseline justify-between flex-wrap gap-3">
                    <div>
                      <div className="text-white font-bold uppercase tracking-tight">{t.name}</div>
                      <div className="text-white/40 text-xs">{t.email}{t.social_handle ? ` · ${t.social_handle}` : ""}</div>
                    </div>
                    <span className="text-[#C9A227] text-[10px] tracking-[0.25em] uppercase font-bold">{t.niche}</span>
                  </div>
                  <p className="text-white/70 text-sm mt-3 leading-relaxed">{t.ask}</p>
                </div>
              ))}
            </div>
          )}

          {tab === "payments" && (
            <div className="space-y-2">
              {payments.length === 0 ? (
                <p className="text-white/40 py-10 text-center text-sm">No payments yet.</p>
              ) : payments.map((p) => (
                <div key={p.id} className="glass border border-[#C9A227]/15 p-4 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm items-center">
                  <div className="text-white/60 text-xs truncate">{p.session_id}</div>
                  <div className="text-white tabular-nums">${p.amount.toFixed(2)}</div>
                  <div className={`text-[10px] tracking-widest uppercase font-bold ${
                    p.payment_status === "paid" ? "text-green-400" :
                    p.payment_status === "initiated" ? "text-yellow-400" : "text-white/40"
                  }`}>{p.payment_status}</div>
                  <div className="text-[#C9A227] text-[10px] tracking-widest uppercase">{p.purpose}</div>
                  <div className="text-white/40 text-xs">{p.created_at?.slice(0, 16)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}

const StatCard = ({ icon, label, value, sub }) => (
  <div className="glass border border-[#C9A227]/20 p-5" data-testid={ADMIN.statsCard}>
    <div className="text-[#C9A227] mb-3">{icon}</div>
    <div className="text-3xl font-black text-white tabular-nums">{value}</div>
    <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A227] mt-1 font-bold">{label}</div>
    {sub && <div className="text-[10px] tracking-widest uppercase text-white/40 mt-1">{sub}</div>}
  </div>
);

const SplitCard = ({ label, value, accent }) => (
  <div className={`p-5 border ${accent ? "border-[#C9A227] bg-[#C9A227]/5" : "border-[#C9A227]/20"}`}>
    <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A227] font-bold">{label}</div>
    <div className={`text-3xl font-black tabular-nums mt-2 ${accent ? "gold-shimmer" : "text-white"}`}>${(value || 0).toFixed(2)}</div>
  </div>
);

const PipelineRow = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-white/60">{label}</span>
    <span className="text-white font-black tabular-nums">{value}</span>
  </div>
);

const Stat = ({ label, v }) => (
  <div>
    <div className="text-[9px] tracking-[0.25em] uppercase text-white/40">{label}</div>
    <div className="text-white font-bold tabular-nums">{v}</div>
  </div>
);
